import { useState } from "react";
import { useStore } from "zustand";
import PropTypes from "prop-types";
import {
  Text,
  Card,
  CardPreview,
  ActionButton,
  Badge,
  Button,
  DropZone,
  FileTrigger,
  Tooltip,
  TooltipTrigger,
  mergeStyles,
} from "@react-spectrum/s2";
import { Focusable } from "@react-aria/interactions";
import { Button as AriaButton } from "react-aria-components";
import { usePress } from "@react-aria/interactions";
import EditIcon from "@react-spectrum/s2/icons/Edit";
import InfoCircleIcon from "@react-spectrum/s2/icons/InfoCircle";
import AlertTriangleIcon from "@react-spectrum/s2/icons/AlertTriangle";
import ImageBackgroundRemove from "@react-spectrum/s2/icons/ImageBackgroundRemove";
import ImageIcon from "@react-spectrum/s2/illustrations/linear/Image";
import PlayIcon from "@react-spectrum/s2/icons/Play";
import BugIcon from "@react-spectrum/s2/icons/Bug";
import GoogleGeminiIcon from "icon:@app/assets/GoogleGemini.svg";
import AdobeFireflyIcon from "icon:@app/assets/AdobeFirefly.svg";
import AdobePhotoshopIcon from "icon:@app/assets/AdobePhotoshop.svg";

import { store } from "@app/store";

import { S3Image } from "@app/components/S3Image";
import { S3Video } from "@app/components/S3Video";
import { HoverState } from "@app/components/HoverState";
import {
  IMAGE_DROP_ZONE_OUTSET,
  IMAGE_DROP_ZONE_STYLE,
  UploadHoverBorder,
  UploadHoverOverlay,
  getImageDragOperation,
} from "@app/components/ImageAsset";
import {
  REFERENCE_KIND,
  getMissingReferenceKind,
  getReferenceTitle,
  getReferenceTitleParts,
} from "@app/hooks/imageVariations/resolve";
import { useIsDevMode } from "@app/hooks/useRoles";
import { VariationProgress } from "./VariationProgress";
import { VARIATION_PROGRESS_STAGE } from "./wizardSteps";

import { style, css } from "@react-spectrum/s2/style" with { type: "macro" };

/**
 * A variation's image or video preview. `aspectRatio` (a CSS `aspect-ratio`,
 * e.g. `"1 / 1"` or `1.5`) overrides the ratio otherwise taken from the asset's
 * size, or 16:9 for a video. `isOutdated` flags the preview, like a failed
 * generation is, when the campaign it was generated from has since changed.
 * `isIdle` marks a variation that was never generated and is waiting to be
 * asked for: it shows no progress, only its `children`. `isInDropZone` aligns
 * the hover and warning outlines with the dashed border of the image drop zone
 * it sits in. `showProgressLabel` turns off the progress label, for a card too
 * small to hold it.
 */
export const ImageVariationCard = ({
  variation,
  asset: _asset,
  assets = [],
  isLoading,
  progress,
  aspectRatio,
  isOutdated = false,
  isIdle = false,
  isInDropZone = false,
  showProgressLabel = true,
  onEdit,
  styles = "",
  children,
}) => {
  // Without `onEdit` there is nothing to press or hover for.
  const { pressProps, isPressed } = usePress({
    onPress: () => onEdit?.(variation),
    isDisabled: !onEdit,
  });

  const fallbackAsset = _asset?.isSkipped
    ? assets.find((a) => a.name === variation?.fallbackKey)
    : null;

  const asset = fallbackAsset ?? _asset;

  // A failed generation, or one the campaign has moved on from.
  // Not while a new image is on its way, which supersedes the flagged one.
  const isFlagged = !isLoading && (!!variation?.data?.errorCode || isOutdated);

  const ModelIcon = asset?.model?.startsWith("nano-banana")
    ? GoogleGeminiIcon
    : asset?.model === "photoshop"
      ? AdobePhotoshopIcon
      : asset?.model?.startsWith("image") || asset?.model === "video"
        ? AdobeFireflyIcon
        : null;

  return (
    <div
      {...pressProps}
      className={style({
        transition: "transform",
        willChange: "transform",
        transform: {
          default: "none",
          isPressed: "perspective(28.125vw) translate3d(0, 0, -4px)",
        },
      })({ isPressed })}
    >
      <Card
        variant="quiet"
        styles={mergeStyles(
          style({ boxShadow: "elevated", width: "full" }),
          styles,
        )}
      >
        <CardPreview>
          {asset?.type === "video" ? (
            <S3Video
              filePath={fallbackAsset?.filePath ?? asset?.filePath}
              src={fallbackAsset?.src ?? asset?.src}
              isLoading={isLoading}
              className={style({
                display: "block",
                objectFit: { default: "cover", isFixedAspectRatio: "contain" },
                width: "full",
                aspectRatio: "16 / 9",
              })({ isFixedAspectRatio: !!aspectRatio })}
              style={aspectRatio ? { aspectRatio } : undefined}
            />
          ) : (
            <S3Image
              alt={asset?.name}
              filePath={fallbackAsset?.filePath ?? asset?.filePath}
              isLoading={isLoading}
              styles={style({
                objectFit: { default: "cover", isFixedAspectRatio: "contain" },
              })({ isFixedAspectRatio: !!aspectRatio })}
              UNSAFE_style={
                aspectRatio
                  ? { aspectRatio }
                  : asset?.size
                    ? {
                        aspectRatio: `${asset.size.width} / ${asset.size.height}`,
                      }
                    : {}
              }
            />
          )}
          {!variation?.data?.errorCode &&
            !isIdle &&
            (isLoading || !asset?.filePath) && (
              <div
                className={style({
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  size: "full",
                  position: "absolute",
                  top: 0,
                  insetStart: 0,
                })}
              >
                <VariationProgress
                  progress={progress}
                  showLabel={showProgressLabel}
                />
              </div>
            )}
          {isFlagged && (
            <div
              className={style({
                position: "absolute",
                top: 2,
                bottom: 2,
                insetStart: 2,
                insetEnd: 2,
                outlineWidth: 2,
                outlineStyle: { default: "solid", isInDropZone: "none" },
                outlineColor: "notice-500",
                borderRadius: "default",
              })({ isInDropZone })}
            >
              <Badge
                variant="notice"
                styles={style({ position: "absolute", top: 8, insetStart: 8 })}
              >
                <AlertTriangleIcon />
              </Badge>
            </div>
          )}
          {!!asset?.filePath && _asset?.type === "video" && (
            <div
              className={style({
                position: "absolute",
                top: "50%",
                insetStart: "50%",
                translate: "-50%",
              })}
            >
              <Button aria-label="Video type" size="S" staticColor="auto">
                <PlayIcon />
              </Button>
            </div>
          )}
          {children}
        </CardPreview>

        {!isLoading && !!asset?.filePath && !!onEdit && (
          // In a drop zone, the hover outline is drawn over the zone's dashed
          // border, while what it shows stays within the card.
          <div
            className={style({ position: "absolute", inset: 0 })}
            style={
              isInDropZone ? { inset: -IMAGE_DROP_ZONE_OUTSET } : undefined
            }
          >
            <HoverState
              isTinted
              renderBehavior="always"
              size={isInDropZone ? "XL" : "L"}
              styles={style({
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "auto",
              })}
            >
              <div
                className={style({
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                })}
                style={
                  isInDropZone ? { inset: IMAGE_DROP_ZONE_OUTSET } : undefined
                }
              >
                <ActionButton
                  aria-label="Edit variation"
                  staticColor="auto"
                  // onPress={() => onEdit?.(variation)}
                  UNSAFE_className={style({
                    pointerEvents: "none",
                  })}
                >
                  {variation.data?.visualPrompt ? (
                    <EditIcon />
                  ) : (
                    <InfoCircleIcon />
                  )}
                </ActionButton>
                {!!ModelIcon && (
                  <ModelIcon
                    styles={style({
                      position: "absolute",
                      top: 8,
                      insetEnd: 8,
                      zIndex: -1,
                    })}
                  />
                )}
              </div>
            </HoverState>
          </div>
        )}

        {isFlagged && isInDropZone && (
          // Drawn over the drop zone's dashed border, which the preview
          // (clipping its content) cannot reach.
          <div
            className={style({
              position: "absolute",
              borderRadius: "xl",
              outlineWidth: 2,
              outlineOffset: -2,
              outlineStyle: "solid",
              outlineColor: "notice-500",
              pointerEvents: "none",
            })}
            style={{ inset: -IMAGE_DROP_ZONE_OUTSET }}
          />
        )}
      </Card>
    </div>
  );
};

ImageVariationCard.propTypes = {
  variation: PropTypes.object,
  asset: PropTypes.object,
  assets: PropTypes.arrayOf(PropTypes.object),
  isLoading: PropTypes.bool,
  progress: PropTypes.object,
  aspectRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOutdated: PropTypes.bool,
  isIdle: PropTypes.bool,
  isInDropZone: PropTypes.bool,
  showProgressLabel: PropTypes.bool,
  onEdit: PropTypes.func,
  styles: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  children: PropTypes.node,
};

/**
 * The reference image(s) a group's variations are generated from.
 *
 * A single reference fills the card, as it always has. A group built from
 * several images shows them as smaller thumbnails in an auto-fill grid, each
 * one openable — and each with its own loading state, which covers both a
 * reference still being generated and one still being described.
 */
export const ReferenceThumbnail = ({
  asset,
  src,
  isLoading,
  progress,
  colorScheme,
  onEdit,
  children,
}) => {
  const { pressProps } = usePress({
    onPress: () => onEdit?.(asset),
    isDisabled: !onEdit,
  });

  return (
    <div
      {...pressProps}
      className={style({
        position: "relative",
        borderRadius: "sm",
        overflow: "hidden",
        backgroundColor: "gray-100",
        aspectRatio: "1 / 1",
      })}
    >
      <S3Image
        alt={asset?.name}
        filePath={asset?.filePath}
        // Already presigned in one batched request by the parent.
        src={src}
        styles={style({
          objectFit: "cover",
          width: "full",
          height: "full",
        })}
      />
      {isLoading && (
        <div
          className={style({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            size: "full",
            position: "absolute",
            top: 0,
            insetStart: 0,
            backgroundColor: {
              colorScheme: {
                light: "white/65",
                dark: "black/65",
                default: "transparent",
              },
            },
          })({ colorScheme })}
        >
          <VariationProgress progress={progress} size={16} showLabel={false} />
        </div>
      )}
      {children}
    </div>
  );
};

ReferenceThumbnail.propTypes = {
  asset: PropTypes.object,
  src: PropTypes.string,
  isLoading: PropTypes.bool,
  progress: PropTypes.object,
  colorScheme: PropTypes.string,
  onEdit: PropTypes.func,
};

/**
 * The title of a reference — what it is — in a tooltip over it: its label,
 * then its name (see `getReferenceTitleParts`). The
 * thumbnail itself does nothing, so it is made focusable for the tooltip to
 * reach keyboard users too, and named for assistive technology.
 */
const ReferenceTooltip = ({ asset, templateData, children }) => {
  const { label, name } = getReferenceTitleParts(asset, templateData);

  return (
    <TooltipTrigger delay={0}>
      <Focusable>
        <div
          role="img"
          aria-label={getReferenceTitle(asset, templateData)}
          tabIndex={0}
          className={style({ display: "block", width: "full" })}
        >
          {children}
        </div>
      </Focusable>
      <Tooltip>
        {label ? (
          <>
            {label}{" "}
            <Text
              styles={style({
                fontSize: "ui-xs",
                color: "disabled",
                whiteSpace: "nowrap",
              })}
            >
              ({name})
            </Text>
          </>
        ) : (
          name
        )}
      </Tooltip>
    </TooltipTrigger>
  );
};

ReferenceTooltip.propTypes = {
  asset: PropTypes.object,
  templateData: PropTypes.object,
  children: PropTypes.node,
};

const ReferenceBadge = () => (
  <Badge
    size="XS"
    variant="informative"
    fillStyle="bold"
    styles={style({
      position: "absolute",
      top: 8,
      insetStart: 8,
    })}
    UNSAFE_className={style({ opacity: 0.65 })}
  >
    <ImageBackgroundRemove />
    <Text>Reference</Text>
  </Badge>
);

/**
 * What stands in for a reference that is a template asset nobody selected: it
 * is not being generated, so there is no progress to show — only that it was
 * not selected, and that it is `isRequired` when it has to be. It takes the
 * file the user drops or picks (`onUpload(items, assetName)`); without it,
 * it is just the info. `variant="tile"` is one of several references.
 */
export const ReferencePlaceholder = ({
  asset,
  isRequired = false,
  variant = "card",
  aspectRatio,
  onUpload,
}) => {
  const [isHovered, setHovered] = useState(false);
  const isTile = variant === "tile";
  const text = isRequired ? "Reference required" : "No reference selected";
  const borderRadius = isTile ? "0.5rem" : IMAGE_DROP_ZONE_STYLE.borderRadius;
  const padding = isTile ? 2 : IMAGE_DROP_ZONE_STYLE.padding;

  const content = (
    <div
      className={style({
        boxSizing: "border-box",
        size: "full",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        padding: 4,
        textAlign: "center",
      })}
      style={{
        aspectRatio: isTile ? "1 / 1" : (aspectRatio ?? "16 / 9"),
      }}
    >
      <ImageIcon
        styles={style({ size: { default: 48, isTile: 24 } })({ isTile })}
      />
      <Text
        styles={style({
          font: { default: "title-sm", isTile: "detail-sm" },
          color: "neutral-subdued",
        })({ isTile })}
      >
        {text}
      </Text>
      {!isTile && !!onUpload && (
        <Text styles={style({ font: "ui-sm", color: "neutral-subdued" })}>
          Drop or select an image
        </Text>
      )}
    </div>
  );

  // The warning of a required reference: the outline over the dashed border,
  // as an outdated image has it, and the badge.
  const warning = isRequired && (
    <div
      className={style({
        position: "absolute",
        outlineWidth: 2,
        outlineStyle: "solid",
        outlineColor: "notice-500",
        outlineOffset: -2,
        pointerEvents: "none",
      })}
      style={{ inset: -2, borderRadius }}
    >
      <Badge
        variant="notice"
        size={isTile ? "XS" : "M"}
        styles={style({ position: "absolute", top: 8, insetStart: 8 })}
      >
        <AlertTriangleIcon />
      </Badge>
    </div>
  );

  if (!onUpload) {
    return (
      <div
        className={style({
          boxSizing: "border-box",
          position: "relative",
          width: "full",
          borderWidth: 2,
          borderStyle: "dashed",
          borderColor: "gray-300",
        })}
        style={{ padding, borderRadius }}
      >
        {content}
        {warning}
      </div>
    );
  }

  return (
    <DropZone
      aria-label={`${text}: drop an image for ${asset?.name}`}
      getDropOperation={getImageDragOperation}
      onDrop={(event) => onUpload(event.items, asset?.name)}
      UNSAFE_style={{ padding, borderRadius }}
      styles={style({ width: "full" })}
    >
      <FileTrigger
        acceptedFileTypes="image/*"
        onSelect={(files) => onUpload(files, asset?.name)}
      >
        <AriaButton
          className={style({
            boxSizing: "border-box",
            position: "relative",
            size: "full",
            padding: 0,
            borderWidth: 0,
            backgroundColor: "transparent",
            overflow: "hidden",
          })}
          style={{ borderRadius: isTile ? "0.375rem" : "0.625rem" }}
          onHoverChange={setHovered}
        >
          {content}
          <UploadHoverOverlay isHovered={isHovered} compact={isTile} />
        </AriaButton>
      </FileTrigger>
      <UploadHoverBorder isHovered={isHovered} borderRadius={borderRadius} />
      {warning}
    </DropZone>
  );
};

ReferencePlaceholder.propTypes = {
  asset: PropTypes.object,
  isRequired: PropTypes.bool,
  variant: PropTypes.oneOf(["card", "tile"]),
  aspectRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUpload: PropTypes.func,
};

/**
 * Whether a reference image is still loading, and what to show meanwhile. A
 * reference can itself be a generated variation, so it is "loading" while it
 * is still pending/generating — and afterwards while it is being described,
 * when the template needs image labeling.
 *
 * A reference with no image that is a template asset instead of a variation
 * (see `getMissingReferenceKind`, from `templateData`) is not generated, so it
 * is `isMissing` — and `isRequired` when it has to be selected — rather than
 * loading. Unless the video is still loading: it may be there yet.
 */
export const getReferenceAssetState = (
  asset,
  { progressByKey, isLoading, skipImageLabeling, isLabeled, templateData } = {},
) => {
  const progress = progressByKey?.[asset?.name];

  if (progress) {
    return { isLoading: true, progress };
  }

  if (!asset?.filePath) {
    const kind =
      templateData && asset?.name
        ? getMissingReferenceKind(asset.name, templateData)
        : REFERENCE_KIND.GENERATED;

    if (kind !== REFERENCE_KIND.GENERATED) {
      return isLoading
        ? { isLoading: true, progress: null }
        : {
            isLoading: false,
            progress: null,
            isMissing: true,
            isRequired: kind === REFERENCE_KIND.REQUIRED,
          };
    }

    return {
      isLoading: true,
      progress: {
        stage: VARIATION_PROGRESS_STAGE.WAITING,
        label: "Generating reference",
      },
    };
  }

  if (isLoading) {
    return { isLoading: true, progress: null };
  }

  if (
    !skipImageLabeling &&
    !(isLabeled ? isLabeled(asset) : !!asset?.labeling?.description)
  ) {
    return {
      isLoading: true,
      progress: { stage: VARIATION_PROGRESS_STAGE.LABELING },
    };
  }

  return { isLoading: false, progress: null };
};

/**
 * The reference image(s) of a variation, badged as such. One reference fills
 * its card — in `aspectRatio` when given — while several are shown as
 * thumbnails in a single card. `showBadge` turns the badge off for callers
 * that label the references themselves.
 *
 * With `templateData`, a reference that is a template asset nobody selected is
 * not "generating": it is a `ReferencePlaceholder`, taking a file through
 * `onUpload(items, assetName)`.
 *
 * Without `onEdit` the references are static — no hover, no press. With
 * `showTooltips` each one is named by a tooltip (see `getReferenceTitle`).
 * `showProgressLabel` off keeps the progress of a single reference to its
 * loader, for a thumbnail too small to hold a label.
 */
export const ReferenceImages = ({
  assets = [],
  urlByPath,
  isLoading,
  progressByKey,
  skipImageLabeling,
  isLabeled,
  templateData,
  colorScheme,
  aspectRatio,
  showBadge = true,
  showProgressLabel = true,
  showTooltips = false,
  onEdit,
  onUpload,
}) => {
  const withTooltip = (asset, node) =>
    showTooltips ? (
      <ReferenceTooltip
        key={asset?.name}
        asset={asset}
        templateData={templateData}
      >
        {node}
      </ReferenceTooltip>
    ) : (
      node
    );

  const getState = (asset) =>
    getReferenceAssetState(asset, {
      progressByKey,
      isLoading,
      skipImageLabeling,
      isLabeled,
      templateData,
    });

  if (assets.length <= 1) {
    const asset = assets[0];
    const {
      isLoading: isAssetLoading,
      progress,
      isMissing,
      isRequired,
    } = getState(asset);

    if (isMissing) {
      return (
        <ReferencePlaceholder
          asset={asset}
          isRequired={isRequired}
          aspectRatio={aspectRatio}
          onUpload={onUpload}
        />
      );
    }

    return withTooltip(
      asset,
      <ImageVariationCard
        variation={{ key: asset?.name }}
        asset={asset}
        isLoading={isLoading}
        progress={progress}
        aspectRatio={aspectRatio}
        showProgressLabel={showProgressLabel}
        onEdit={onEdit ? () => onEdit(asset) : undefined}
        styles={style({ width: "full" })}
      >
        {showBadge && <ReferenceBadge />}
        {/* `ImageVariationCard` renders its own progress while the file is
            missing or the video is loading — this overlay covers the rest
            (regenerating and labeling), where it already shows the image. */}
        {isAssetLoading && !isLoading && !!asset?.filePath && (
          <div
            className={style({
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              size: "full",
              position: "absolute",
              top: 0,
              insetStart: 0,
              backgroundColor: {
                colorScheme: {
                  light: "white/65",
                  dark: "black/65",
                  default: "transparent",
                },
              },
            })({ colorScheme })}
          >
            <VariationProgress
              progress={progress}
              showLabel={showProgressLabel}
            />
          </div>
        )}
      </ImageVariationCard>,
    );
  }

  return (
    <Card
      variant="quiet"
      styles={style({ boxShadow: "elevated", width: "full" })}
    >
      <CardPreview>
        <div
          className={mergeStyles(
            style({
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 4,
              padding: 4,
              width: "full",
              boxSizing: "border-box",
            }),
            css(`
&:has(> :nth-child(2)),
&:has(> :nth-child(3)),
&:has(> :nth-child(4)) {
  grid-template-columns: repeat(2, 1fr);
}

&:has(> :nth-child(5)) {
  grid-template-columns: repeat(3, 1fr);
}
`),
          )}
        >
          {assets.map((asset) => {
            const {
              isLoading: isAssetLoading,
              progress,
              isMissing,
              isRequired,
            } = getState(asset);

            if (isMissing) {
              return (
                <ReferencePlaceholder
                  key={asset?.name}
                  asset={asset}
                  isRequired={isRequired}
                  variant="tile"
                  onUpload={onUpload}
                />
              );
            }

            return withTooltip(
              asset,
              <ReferenceThumbnail
                key={asset?.name}
                asset={asset}
                src={urlByPath?.get(asset?.filePath)}
                isLoading={isAssetLoading}
                progress={progress}
                colorScheme={colorScheme}
                onEdit={onEdit}
              />,
            );
          })}
        </div>
        {showBadge && <ReferenceBadge />}
      </CardPreview>
    </Card>
  );
};

ReferenceImages.propTypes = {
  assets: PropTypes.arrayOf(PropTypes.object),
  urlByPath: PropTypes.instanceOf(Map),
  isLoading: PropTypes.bool,
  progressByKey: PropTypes.object,
  skipImageLabeling: PropTypes.bool,
  isLabeled: PropTypes.func,
  templateData: PropTypes.object,
  colorScheme: PropTypes.string,
  aspectRatio: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  showBadge: PropTypes.bool,
  showProgressLabel: PropTypes.bool,
  showTooltips: PropTypes.bool,
  onEdit: PropTypes.func,
  onUpload: PropTypes.func,
};

/**
 * One reference image group: the image(s) the group is generated from, and a
 * card per variation built from them.
 *
 * Purely presentational — every bit of state it renders is produced by
 * `useImageVariations` (see `@app/hooks/imageVariations`), which the wizard
 * owns so generation keeps running independently of what is on screen.
 */
export const ImageVariations = ({
  group,
  assets,
  referenceUrlByPath,
  generatingKeys,
  skipImageLabeling,
  isLabeled,
  templateData,
  isLoading,
  onEditVariation,
  onRegenerate,
  onUploadReference,
}) => {
  const colorScheme = useStore(store, (state) => state.colorScheme);
  const isDevMode = useIsDevMode();

  // Subscribed here rather than passed down: generation progress ticks often,
  // and only these cards render it.
  const progressByKey = useStore(
    store,
    (state) => state.imageVariationProgress,
  );

  const variations = group?.variations ?? [];
  const referenceAssets = group?.referenceAssets ?? [];
  const hasReference = !!group?.hasReference;

  return (
    <div
      className={style({
        minWidth: 0,
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: "0%",
        display: "flex",
        flexDirection: {
          lg: "row",
          default: "column",
        },
        alignItems: "start",
        gap: 16,
        position: "relative",
      })}
    >
      {isDevMode && !!onRegenerate && (
        <div
          className={style({
            position: "absolute",
            top: -32,
            insetEnd: -32,
            opacity: 0.5,
          })}
        >
          <ActionButton
            size="XS"
            staticColor="auto"
            onPress={() => onRegenerate()}
          >
            <BugIcon />
            <Text>Regenerate</Text>
          </ActionButton>
        </div>
      )}
      <div
        className={style({
          maxWidth: "full",

          display: "inline-grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, max-content))",
          justifyContent: "center",
          alignItems: "center",
          gap: 16,
        })}
        style={{
          width: `calc(240px * ${Math.min(variations.length + (hasReference ? 1 : 0), 4)} + ${2 * Math.min(variations.length + (hasReference ? 0 : -1), 3)}rem)`,
          maxWidth: "100%",
        }}
      >
        {hasReference && (
          <div className={style({ width: 240, maxWidth: "full" })}>
            <ReferenceImages
              assets={referenceAssets}
              urlByPath={referenceUrlByPath}
              isLoading={isLoading}
              progressByKey={progressByKey}
              skipImageLabeling={skipImageLabeling}
              isLabeled={isLabeled}
              templateData={templateData}
              colorScheme={colorScheme}
              onUpload={onUploadReference}
              onEdit={(asset) =>
                onEditVariation?.({
                  key: asset?.name,
                  referenceKey: asset?.name,
                  isReferenceAsset: true,
                  type: "original",
                  data: {
                    ...asset?.labeling,
                  },
                })
              }
            />
          </div>
        )}
        {variations.map((variation) => (
          <div
            key={variation.key}
            className={style({ width: 240, maxWidth: "full" })}
          >
            <ImageVariationCard
              variation={variation}
              asset={assets?.find((asset) => asset.name === variation.key)}
              assets={assets}
              isLoading={!!generatingKeys?.has(variation.key)}
              progress={progressByKey?.[variation.key]}
              isParentHovered={true}
              onEdit={onEditVariation}
              styles={style({ width: "full" })}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

ImageVariations.propTypes = {
  group: PropTypes.shape({
    id: PropTypes.string,
    referenceKeys: PropTypes.arrayOf(PropTypes.string),
    referenceAssets: PropTypes.arrayOf(PropTypes.object),
    variations: PropTypes.arrayOf(PropTypes.object),
    hasReference: PropTypes.bool,
  }).isRequired,
  assets: PropTypes.arrayOf(PropTypes.object),
  referenceUrlByPath: PropTypes.instanceOf(Map),
  generatingKeys: PropTypes.instanceOf(Set),
  skipImageLabeling: PropTypes.bool,
  isLabeled: PropTypes.func,
  templateData: PropTypes.object,
  isLoading: PropTypes.bool,
  onEditVariation: PropTypes.func,
  onRegenerate: PropTypes.func,
  onUploadReference: PropTypes.func,
};
